<template>
  <div class="autocomplete-wrapper" ref="autocomplete">
    <!-- Input Container -->
    <div class="input-container" @click="handleContainerClick" ref="container">
      <input
        ref="input"
        type="text"
        class="form-control premium-input"
        v-model="query"
        @input="onInput"
        @focus="onFocus"
        :disabled="disabled"
        :placeholder="$t('search') || 'Search...'"
      />
      
      <!-- Actions (Clear & Chevron) -->
      <div class="actions">
        <button 
          v-if="query && !disabled" 
          type="button" 
          class="clear-btn" 
          @click.stop="clearInput"
          title="Clear"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
        <div class="chevron" :class="{ 'open': !hideDropDown }" @click.stop="toggleDropdown">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
      </div>
    </div>

    <!-- Dropdown Menu (Portal) -->
    <div 
      ref="dropdown"
      v-show="shouldShowDropdown" 
      class="dropdown-menu-portal"
      @scroll.passive="handleScroll"
    >
      <ul v-if="filteredSuggestions.length > 0" class="suggestions-list">
        <!-- Inventories -->
        <template v-if="entity === 'inventories'">
          <li v-for="suggestion in filteredSuggestions" :key="suggestion.id" @click="selectItem(suggestion)" class="suggestion-item">
            <div class="item-content">
              <img v-if="suggestion?.product?.image" :src="suggestion.product.image" class="product-image" alt="Product">
              <div class="item-details">
                <span class="item-label">{{ suggestion.product?.label }}</span>
                <span class="item-meta">
                  <span class="price">{{ suggestion.price }} {{ business.currency }}</span>
                  <span v-if="suggestion.warehouse" class="warehouse-badge">{{ suggestion.warehouse.name }}</span>
                </span>
              </div>
            </div>
          </li>
        </template>

        <!-- Invoices, Orders, etc. -->
        <template v-else-if="['supplier-invoices', 'invoices', 'orders', 'purchases'].includes(entity)">
          <li v-for="suggestion in filteredSuggestions" :key="suggestion.id" @click="selectItem(suggestion)" class="suggestion-item">
            <span class="item-text">{{ suggestion.reference }}</span>
          </li>
        </template>

        <!-- Default (Clients, Suppliers, etc.) -->
        <template v-else>
          <li v-for="suggestion in filteredSuggestions" :key="suggestion.id" @click="selectItem(suggestion)" class="suggestion-item">
            <span class="item-text">{{ suggestion.name }}</span>
          </li>
        </template>
      </ul>

      <!-- Loading State -->
      <div v-if="loading" class="status-message">
        <div class="spinner"></div>
        <span>{{ $t('loading') || 'Loading...' }}</span>
      </div>

      <!-- Minimum Search Length State -->
      <div v-else-if="isQueryTooShort" class="status-message">
        {{ $t('type_at_least_3_characters') }}
      </div>
      
      <!-- No Results State -->
      <div v-else-if="filteredSuggestions.length === 0 && isRequested" class="status-message">
        {{ $t('no_results_found') || 'No results found' }}
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';

export default {
  props : {
       uid: {
            type: Number,
            required: false
        },
        defaultValue: {
            type: String,
            required: false
        },
        emitEvent : {
            type : String,
            required: true,
        },
        entity : {
          type : String,
          required : true
        },
        dislaySuggestions : {
            type : Boolean,
            default: false,
        },
        excludedProductIds: {
            type: Array,
            default: () => []
        },
          disabled : {
            type : Boolean,
            default: false,
        }
  },
  data() {
    return {
      query: "",
      suggestions: [],
      selected: null,
      elementId: this.uid,
      loading: false,
      hideDropDown : true,
      isRequested : false,
      isInitialSuggestions : false,
      page: 1,
      perPage: 10,
      hasMore: true,
      requestId: 0,
      inputDebounceTimer: null
    };
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS || { currency: '$' };
    },
    isQueryTooShort() {
      const queryLength = this.query.trim().length;
      return queryLength > 0 && queryLength < 3;
    },
    filteredSuggestions() {
      if (this.entity !== 'inventories' || !this.excludedProductIds.length) {
        return this.suggestions || [];
      }

      const excludedIds = new Set(this.excludedProductIds.map(id => String(id)));
      return (this.suggestions || []).filter(suggestion => {
        const productId = suggestion?.product?.id;
        return productId == null || !excludedIds.has(String(productId));
      });
    },
    shouldShowDropdown() {
      if (this.hideDropDown) return false;
      return this.isQueryTooShort ||
        this.filteredSuggestions.length > 0 ||
        this.loading ||
        this.isRequested;
    }
  },
  watch: {
    defaultValue: {
      immediate: true,
      handler(val) {
        if (val !== undefined && val !== this.query) {
          this.query = val || '';
        }
      }
    },
    dislaySuggestions: {
      immediate: true,
      handler(val) {
        if (val && !this.query && this.suggestions.length === 0) {
          this.getInitialSuggestions();
        }
      }
    }
  },
  methods: {

    async fetchSuggestions() {
      if (this.query.trim().length > 2) {
        await this.loadSuggestions({ reset: true, initial: false });
      } else {
        this.requestId += 1;
        this.loading = false;
        this.suggestions = [];
        this.page = 1;
        this.hasMore = true;
        this.isRequested = false;
        this.isInitialSuggestions = false;
      }
    },
    async getInitialSuggestions(){
      await this.loadSuggestions({ reset: true, initial: true });
    },
    async loadSuggestions({ reset = false, initial = false } = {}){
      if (!reset && (this.loading || !this.hasMore)) return;

      const keyword = initial ? '' : this.query.trim();
      if (!initial && keyword.length <= 2) return;

      const targetPage = reset ? 1 : this.page + 1;
      const currentRequestId = ++this.requestId;

      if (reset) {
        this.suggestions = [];
        this.page = 1;
        this.hasMore = true;
        this.$nextTick(() => {
          if (this.$refs.dropdown) {
            this.$refs.dropdown.scrollTop = 0;
          }
        });
      }

      this.loading = true;
      this.isRequested = true;
      this.hideDropDown = false;
      this.isInitialSuggestions = initial;

      try {
        const params = new URLSearchParams();
        params.append('page', targetPage);
        params.append('per_page', this.perPage);
        if (keyword) {
          params.append('keyword', keyword);
        }

        const url = `${process.env.VUE_APP_API}/${this.entity}?${params.toString()}`;
        const response = await helper.customGet(url);

        if (currentRequestId !== this.requestId) return;

        const newSuggestions = Array.isArray(response.data.data) ? response.data.data : [];

        if (reset) {
          this.suggestions = newSuggestions;
        } else {
          const existingIds = new Set(this.suggestions.map(suggestion => suggestion.id));
          const uniqueSuggestions = newSuggestions.filter(suggestion => !existingIds.has(suggestion.id));
          this.suggestions = [...this.suggestions, ...uniqueSuggestions];
        }

        this.page = targetPage;

        const total = Number(response.data.count);
        this.hasMore = Number.isFinite(total)
          ? targetPage * this.perPage < total
          : newSuggestions.length === this.perPage;
      } catch (error) {
        if (currentRequestId === this.requestId && reset) {
          this.suggestions = [];
        }
      } finally {
        if (currentRequestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    handleScroll(event) {
      const dropdown = event.target;
      const distanceFromBottom = dropdown.scrollHeight - dropdown.scrollTop - dropdown.clientHeight;

      if (distanceFromBottom <= 32) {
        this.loadSuggestions({
          reset: false,
          initial: this.isInitialSuggestions
        });
      }
    },
    onInput() {
      this.requestId += 1;
      this.loading = false;
      this.suggestions = [];
      this.page = 1;
      this.hasMore = true;
      this.isRequested = false;
      this.isInitialSuggestions = false;

      if (this.query.trim().length > 0) {
        this.hideDropDown = false;
      }

      clearTimeout(this.inputDebounceTimer);
      this.inputDebounceTimer = setTimeout(() => {
        if (!this.query) {
          this.getInitialSuggestions();
        } else {
          this.fetchSuggestions();
        }
      }, 300);
    },
    onFocus() {
      if (!this.query) {
        if (!this.loading) {
          this.getInitialSuggestions();
        }
      } else {
        if (!this.loading && this.suggestions.length === 0 && this.query.length > 2) {
          this.fetchSuggestions();
        } else {
          this.hideDropDown = false;
        }
      }
    },
    handleContainerClick() {
      if (this.disabled) return;
      if (document.activeElement !== this.$refs.input) {
        this.$refs.input.focus();
      } else {
        if (this.hideDropDown) {
          this.hideDropDown = false;
          if (!this.query && this.suggestions.length === 0) {
            this.getInitialSuggestions();
          } else {
            // Dropdown is already shown
          }
        }
      }
    },
    toggleDropdown() {
      if (this.disabled) return;
      if (this.hideDropDown) {
        this.hideDropDown = false;
        this.$refs.input.focus();
        if (!this.query && this.suggestions.length === 0) {
          this.getInitialSuggestions();
        } else {
          // Dropdown is already shown
        }
      } else {
        this.hideDropDown = true;
      }
    },
    selectItem(result) {
      clearTimeout(this.inputDebounceTimer);
      this.$emit(this.emitEvent, { index: this.elementId, result });
      if(this.entity == 'inventories'){
        this.query = result.product.label;
      }else if(this.entity == 'supplier-invoices' || this.entity == 'orders' || this.entity == 'purchases' || this.entity == 'invoices'){
        this.query = result.reference;
      }else {
        this.query = result.name;
      }
      this.selected = result;
      this.requestId += 1;
      this.loading = false;
      this.suggestions = [];
      this.page = 1;
      this.hasMore = true;
      this.hideDropDown = true;
    },
    clearInput() {
      clearTimeout(this.inputDebounceTimer);
      this.requestId += 1;
      this.loading = false;
      this.query = '';
      this.suggestions = [];
      this.selected = null;
      this.page = 1;
      this.hasMore = true;
      this.hideDropDown = true;
      this.$emit(this.emitEvent, { index: this.elementId, clear_input : true });
      this.$nextTick(() => {
        this.getInitialSuggestions();
        this.$refs.input.focus();
      });
    },
    handleClickOutside(event) {
      const isInsideComponent = this.$refs.autocomplete && this.$refs.autocomplete.contains(event.target);
      const isInsideDropdown = this.$refs.dropdown && this.$refs.dropdown.contains(event.target);
      
      if (!isInsideComponent && !isInsideDropdown) {
        this.hideDropDown = true;
      }
    },

  },
  mounted() {
    // If dislaySuggestions is true, we show suggestions even if there is a defaultValue
    if(this.dislaySuggestions && !this.isRequested){
      this.getInitialSuggestions();
    } else if (this.defaultValue == '' || this.defaultValue == undefined) {
      // Optional: if no default value, maybe we don't fetch unless clicked?
      // Original code fetched if displaySuggestions was true AND default was empty.
      // Now we fetch if displaySuggestions is true OR ... wait.
      // If displaySuggestions is false, we don't fetch on mount.
    }

    document.addEventListener('click', this.handleClickOutside);
    
    if(this.defaultValue){
      this.selected = {};
      this.query = this.defaultValue;
    }
  },
  beforeDestroy() {
    clearTimeout(this.inputDebounceTimer);
    this.requestId += 1;
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.autocomplete-wrapper {
  position: relative;
  width: 100%;
  font-family: 'Inter', sans-serif;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.premium-input {
  width: 100%;
  padding: 6px 16px;
  padding-right: 60px; /* Space for actions */
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background-color: #ffffff;
  color: #1e293b;
  font-size: 0.95rem;
  line-height: 1.5;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  outline: none;
}

.premium-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.premium-input:disabled {
  background-color: #f8fafc;
  cursor: not-allowed;
  color: #94a3b8;
}

.actions {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  pointer-events: none;
}

.clear-btn {
  pointer-events: auto;
  background: none;
  border: none;
  padding: 4px;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.clear-btn:hover {
  background-color: #f1f5f9;
  color: #ef4444;
}

.chevron {
  pointer-events: auto;
  color: #64748b;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px;
}

.chevron.open {
  transform: rotate(180deg);
}

/* Dropdown Portal Styles */
.dropdown-menu-portal {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 99999;
  margin-top: 5px;
  max-height: 250px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px;
  box-sizing: border-box;
}

/* Scrollbar styling */
.dropdown-menu-portal::-webkit-scrollbar {
  width: 6px;
}
.dropdown-menu-portal::-webkit-scrollbar-track {
  background: transparent;
}
.dropdown-menu-portal::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 3px;
}

.suggestions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestion-item {
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  color: #334155;
  font-size: 0.9rem;
}

.suggestion-item:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.item-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-label {
  font-weight: 500;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #64748b;
}

.warehouse-badge {
  background-color: #e0e7ff;
  color: #4338ca;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-message {
  padding: 16px;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
